import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Monitor, Mail, PenTool, Camera, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Button from "@/components/Button";
import MaskText from "@/components/MaskText";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { SERVICES } from "@/data/site";
import { fadeUp, stagger, easeLux } from "@/lib/motion";
import Seo from "@/components/Seo";

const ICONS = { Monitor, Mail, PenTool, Camera };

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yBlobA = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yBlobB = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      <Seo
        title=""
        description="Lu Design, estudio creativo en Monterrey. Diseñamos experiencias que cuentan historias: diseño web, invitaciones digitales, diseño gráfico y fotografía profesional."
        path="/"
      />
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex min-h-screen items-center overflow-hidden pt-32 pb-20"
      >
        {/* Parallax decor */}
        <motion.div
          style={{ y: yBlobA }}
          className="pointer-events-none absolute -right-32 top-24 h-[38rem] w-[38rem] rounded-full bg-powder/40 blur-3xl"
        />
        <motion.div
          style={{ y: yBlobB }}
          className="pointer-events-none absolute -left-40 bottom-0 h-[30rem] w-[30rem] rounded-full bg-rosegold/20 blur-3xl"
        />

        <motion.div
          style={{ y: heroTextY, opacity: heroOpacity }}
          className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mb-8 text-xs md:text-sm uppercase tracking-[0.35em] text-rosegold font-sans"
          >
            Estudio creativo
          </motion.p>

          <h1 className="font-serif text-charcoal font-light tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02]">
            <MaskText
              lines={["Diseñamos experiencias", "que cuentan"]}
              delay={0.3}
            />
            <MaskText
              lines={[
                <span key="h" className="italic text-plum">
                  historias.
                </span>,
              ]}
              delay={0.75}
            />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: easeLux }}
            className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between"
          >
            <p className="text-sm md:text-base uppercase tracking-[0.3em] text-charcoal/70 font-sans">
              Diseño <span className="text-rosegold">·</span> Emoción{" "}
              <span className="text-rosegold">·</span> Conexión
            </p>
            <Button to="/portafolio" testId="hero-cta">
              Ver portafolio
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-[0.6rem] uppercase tracking-[0.3em] text-charcoal/40">Descubre</span>
          <span className="h-12 w-px bg-gradient-to-b from-rosegold to-transparent" />
        </motion.div>
      </section>

      {/* SERVICIOS PREVIEW */}
      <section className="relative z-10 bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Reveal>
                <p className="mb-4 text-xs uppercase tracking-[0.3em] text-rosegold">Lo que hacemos</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif text-charcoal font-light tracking-tight text-4xl md:text-5xl lg:text-6xl leading-tight">
                  Cuatro formas de <span className="italic text-plum">conectar</span>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/servicios"
                data-testid="services-preview-link"
                className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-plum transition-colors hover:text-rosegold"
              >
                Ver todos los servicios
                <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>

          <motion.div
            variants={stagger(0, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-rosegold/20 border border-rosegold/20"
          >
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <motion.div key={s.id} variants={fadeUp}>
                  <Link
                    to="/servicios"
                    data-testid={`service-preview-${s.id}`}
                    className="group flex h-full flex-col bg-ivory p-8 lg:p-10 transition-colors duration-500 hover:bg-powder/40"
                  >
                    <span className="mb-8 font-serif text-2xl text-rosegold">{s.number}</span>
                    <Icon className="mb-6 h-8 w-8 text-plum" strokeWidth={1} />
                    <h3 className="font-serif text-2xl md:text-3xl text-charcoal mb-3">{s.title}</h3>
                    <p className="font-sans font-light text-sm leading-relaxed text-charcoal/70">
                      {s.short}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 bg-ivory border-y border-rosegold/20 py-10 md:py-14">
        <Marquee
          testId="home-marquee"
          className="text-plum text-4xl md:text-6xl"
          text="Diseño que conecta e inspira"
        />
      </section>

      {/* MANIFIESTO NUMERADO */}
      <section className="relative z-10 bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="mb-16 max-w-2xl font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-snug text-charcoal">
              Creemos que un buen diseño no solo se ve:{" "}
              <span className="italic text-plum">se siente</span>.
            </p>
          </Reveal>
          <div className="grid gap-12 md:grid-cols-3">
            {[
              { n: "01", t: "Escuchamos", d: "Cada proyecto comienza con tu historia. Entendemos tu esencia antes de trazar una sola línea." },
              { n: "02", t: "Creamos", d: "Traducimos emociones en identidad visual: color, tipografía y detalle con intención." },
              { n: "03", t: "Conectamos", d: "Entregamos piezas que emocionan a tu audiencia y hacen crecer tu marca con alma." },
            ].map((item, i) => (
              <Reveal key={item.n} delay={i * 0.12}>
                <div className="border-t border-rosegold/30 pt-6">
                  <span className="font-serif text-2xl text-rosegold">{item.n}</span>
                  <h3 className="mt-4 font-serif text-2xl md:text-3xl text-plum">{item.t}</h3>
                  <p className="mt-3 font-sans font-light text-sm leading-relaxed text-charcoal/70">
                    {item.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CIERRE / CTA */}
      <section className="relative z-10 overflow-hidden bg-plum py-28 md:py-40">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-rosegold/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-96 w-96 rounded-full bg-powder/10 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-6 md:px-12 text-center">
          <Reveal>
            <p className="mb-6 text-xs uppercase tracking-[0.3em] text-rosegold">¿Comenzamos?</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-ivory tracking-tight text-4xl md:text-5xl lg:text-6xl leading-tight">
              Cuéntanos tu idea y creemos algo{" "}
              <span className="italic">memorable</span> juntas.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 flex justify-center">
              <Button to="/contacto" variant="light" testId="home-closing-cta">
                Hablemos
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
